import type { PublicRuntimeConfig } from 'nuxt/schema'
import { locales } from '@@/shared-components/locales/index'
import type { PrimeVueConfiguration } from 'primevue/config'

export function useLocale() {
  const localeCookieKey = 'locale'
  const dataLangCookieKey = 'data-lang'
  const i18n = tryUseNuxtApp() ? useNuxtApp()?.$i18n : useI18n()
  const { public: config } = useRuntimeConfig()

  const previewLocale = useState<ValueOf<typeof locales> | null>('preview-locale', () => {
    if (import.meta.client) {
      return localStorage.getItem('preview-locale') as ValueOf<typeof locales> | null
    }
    return null
  })
  // BE lang kód pro i18n kód: projektová mapa (runtimeConfig.public.locales,
  // plní ji modul z options.i18n.dataLangs / kompaktní mapy locales) →
  // vestavěná mapa (en → en_GB, …) → identity (kód samotný, např. `sk`).
  const toDataLang = (code: string) =>
    ((config.locales as Record<string, string> | undefined)?.[code] ??
      locales[code as keyof typeof locales] ??
      code) as ValueOf<typeof locales>
  const locale = useState<ValueOf<typeof locales>>('locale', () => toDataLang(i18n.locale.value))
  const dataLang = useState<ValueOf<typeof locales>>('dataLang', () =>
    toDataLang(i18n.locale.value)
  )
  const setLocale = async (
    newLocale: ValueOf<typeof locales>,
    i18nLocale: keyof PublicRuntimeConfig['locales']
  ) => {
    locale.value = newLocale
    const nuxtApp = useNuxtApp()
    await setPrimeVueLocale(nuxtApp.vueApp.config.globalProperties.$primevue.config)
    useCookie(localeCookieKey, { sameSite: true, secure: true }).value = newLocale
    await loadMessages(newLocale, i18nLocale)
    await i18n.setLocale(i18nLocale)
  }

  // Dynamické importy — do bundlu jde jen JSON aktivního jazyka, ostatní
  // zůstávají ve vlastních chuncích. Klíčujeme krátkým kódem, `locale.value`
  // může být dlouhý (en_GB) i krátký/BE formát (en, sk). Fallback en_GB.
  const primeVueLocaleLoaders: Record<
    string,
    () => Promise<{ default: PrimeVueConfiguration['locale'] }>
  > = {
    en: () => import('@@/shared-components/locales/primevue/en_GB.json'),
    cs: () => import('@@/shared-components/locales/primevue/cs_CZ.json'),
    sk: () => import('@@/shared-components/locales/primevue/sk_SK.json'),
    de: () => import('@@/shared-components/locales/primevue/de_DE.json'),
    pl: () => import('@@/shared-components/locales/primevue/pl_PL.json'),
    ru: () => import('@@/shared-components/locales/primevue/ru_RU.json'),
  }

  const setPrimeVueLocale = async (config: PrimeVueConfiguration) => {
    const short = locale.value?.split('_')[0] ?? ''
    const loader = primeVueLocaleLoaders[short] ?? primeVueLocaleLoaders.en!
    config.locale = { ...(await loader()).default } as PrimeVueConfiguration['locale']
  }

  const setDataLang = async (newDatalang: ValueOf<typeof locales>) => {
    dataLang.value = newDatalang
    useCookie(dataLangCookieKey, { sameSite: true, secure: true }).value = newDatalang
  }

  const setPreviewLocale = (locale: ValueOf<typeof locales>) => {
    if (previewLocale.value === locale) {
      previewLocale.value = null
      localStorage.removeItem('preview-locale')
    } else {
      previewLocale.value = locale
      localStorage.setItem('preview-locale', locale)
    }
  }
  const instange = getCurrentInstance()
  let queryClient: ReturnType<typeof useQueryClient> | null = instange ? useQueryClient() : null

  const loadMessages = async (
    lang: ValueOf<typeof locales>,
    i18nLocale: keyof PublicRuntimeConfig['locales']
  ) => {
    try {
      const nuxtApp = useNuxtApp()
      if (!queryClient) {
        queryClient = useQueryClient()
      }
      const messages = await queryClient.fetchQuery(
        messagesQuery(
          { locale: locale.value, dataLang: dataLang.value },
          {
            domain:
              config.translationsDomain ||
              (nuxtApp.$sharedComponentsOptions &&
                'options' in nuxtApp.$sharedComponentsOptions &&
                nuxtApp.$sharedComponentsOptions?.options &&
                'i18n' in nuxtApp.$sharedComponentsOptions?.options &&
                typeof nuxtApp.$sharedComponentsOptions?.options?.i18n === 'object' &&
                'translationsDomain' in nuxtApp.$sharedComponentsOptions?.options?.i18n &&
                nuxtApp.$sharedComponentsOptions?.options?.i18n?.translationsDomain),
            lang,
          }
        )
      )

      if (messages && typeof messages === 'object') {
        i18n.setLocaleMessage(i18nLocale, messages)
      }
      return messages
    } catch (e) {
      console.error(e)
    }
  }

  return {
    locale,
    dataLang,
    previewLocale,
    setLocale,
    setDataLang,
    setPreviewLocale,
    setPrimeVueLocale,
    loadMessages,
  }
}
